<!--
  One line over time with a soft area under it. Hover (or drag a finger) to read any point.
  Values are in micro; labels use compact money.
-->
<script lang="ts">
  import { compactMicro, formatMicro } from "../../lib/format.ts";

  interface Point {
    /** Short label for the x axis and readout, like "Oct 12". */
    label: string;
    value: number;
  }

  let {
    points,
    height = 180,
    color = "var(--series-1)",
    title,
  }: { points: Point[]; height?: number; color?: string; title: string } = $props();

  let width = $state(600);
  let hover = $state<number | null>(null);

  const pad = { top: 12, right: 12, bottom: 24, left: 52 };
  const min = $derived(Math.min(0, ...points.map((p) => p.value)));
  const max = $derived(Math.max(1, ...points.map((p) => p.value)));
  const nice = $derived.by(() => {
    const span = max - min || 1;
    const step = 10 ** Math.floor(Math.log10(span / 3));
    const s = [1, 2, 2.5, 5, 10].map((m) => m * step).find((x) => span / x <= 4) ?? step * 10;
    const lo = Math.floor(min / s) * s;
    const hi = Math.ceil(max / s) * s;
    const ticks: number[] = [];
    for (let v = lo; v <= hi + s / 2; v += s) ticks.push(v);
    return { lo, hi, ticks };
  });
  const iw = $derived(Math.max(10, width - pad.left - pad.right));
  const ih = $derived(height - pad.top - pad.bottom);
  const x = (i: number) => pad.left + (points.length <= 1 ? iw / 2 : (i / (points.length - 1)) * iw);
  const y = (v: number) => pad.top + ih - ((v - nice.lo) / (nice.hi - nice.lo || 1)) * ih;
  const line = $derived(points.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(""));
  const area = $derived(
    points.length ? `${line}L${x(points.length - 1).toFixed(1)},${y(Math.max(nice.lo, 0))}L${x(0).toFixed(1)},${y(Math.max(nice.lo, 0))}Z` : "",
  );
  const xTicks = $derived(points.length <= 1 ? [0] : [0, Math.floor((points.length - 1) / 2), points.length - 1]);

  function move(e: PointerEvent) {
    const r = (e.currentTarget as SVGElement).getBoundingClientRect();
    const px = e.clientX - r.left;
    const i = Math.round(((px - pad.left) / iw) * (points.length - 1));
    hover = Math.max(0, Math.min(points.length - 1, i));
  }
</script>

<figure class="chart" bind:clientWidth={width}>
  <figcaption>{title}</figcaption>
  {#if points.length}
    <svg
      role="img"
      aria-label="{title}: from {formatMicro(points[0]!.value)} to {formatMicro(points.at(-1)!.value)}"
      {width}
      {height}
      onpointermove={move}
      onpointerdown={move}
      onpointerleave={() => (hover = null)}
    >
      {#each nice.ticks as t, ti (ti)}
        <line class="grid" class:zero={t === 0} x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} />
        <text class="ylab" x={pad.left - 8} y={y(t)} dy="0.32em">{compactMicro(t)}</text>
      {/each}
      {#each [...new Set(xTicks)] as i (i)}
        <text class="xlab" x={x(i)} y={height - 6} text-anchor={i === 0 ? "start" : i === points.length - 1 ? "end" : "middle"}>{points[i]!.label}</text>
      {/each}
      <path d={area} fill={color} opacity="0.12" />
      <path d={line} fill="none" stroke={color} stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
      {#if hover !== null}
        {@const p = points[hover]!}
        <line class="cross" x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + ih} />
        <circle cx={x(hover)} cy={y(p.value)} r="5" fill={color} stroke="var(--surface)" stroke-width="2" />
      {:else}
        <circle cx={x(points.length - 1)} cy={y(points.at(-1)!.value)} r="4" fill={color} stroke="var(--surface)" stroke-width="2" />
      {/if}
    </svg>
    {@const shown = points[hover ?? points.length - 1]!}
    <div class="readout" aria-live="polite">
      <strong class="num">{formatMicro(shown.value)}</strong>
      <span class="muted">{hover === null ? "now" : shown.label}</span>
    </div>
  {:else}
    <p class="muted empty">Nothing to show yet.</p>
  {/if}
</figure>

<style>
  .chart {
    position: relative;
    margin: 0;
    min-width: 0;
  }

  figcaption {
    font-weight: 650;
    margin-bottom: 4px;
  }

  svg {
    display: block;
    touch-action: pan-y;
    cursor: crosshair;
    overflow: visible;
  }

  .grid {
    stroke: var(--rule);
    stroke-width: 1;
  }

  .grid.zero {
    stroke: var(--rule-strong);
  }

  .ylab,
  .xlab {
    fill: var(--muted);
    font-size: 11px;
  }

  .ylab {
    text-anchor: end;
  }

  .cross {
    stroke: var(--ink-2);
    stroke-width: 1;
    stroke-dasharray: 3 3;
  }

  .readout {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    gap: 8px;
    align-items: baseline;
    font-size: var(--t-sm);
  }

  .empty {
    padding: 24px 0;
  }
</style>
